import { useState } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MainLayout } from '@/components/layout/MainLayout';
import { FileDropzone, FileUploadProgress, FileList, useDocuments } from '@/features/files';
import { cn } from '@/lib/utils';

export const FilesPage = () => {
  const { refreshDocuments } = useDocuments();
  const [uploadPanelOpen, setUploadPanelOpen] = useState(false);

  return (
    <MainLayout>
      <div className="container mx-auto py-6 px-4 sm:py-8 max-w-5xl">
        <div className="space-y-6 sm:space-y-8">
          {/* Page Header with Upload button */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">My Documents</h1>
              <p className="text-muted-foreground">
                Upload and manage your documents securely
              </p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => setUploadPanelOpen(true)}
                  className="gap-2 shrink-0"
                >
                  <Upload className="h-4 w-4" />
                  Upload Documents
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">Upload documents</p>
                <p className="text-muted-foreground text-xs">Add PDF, DOC, images to your account</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Documents List Section - moved up */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5 text-primary" />
                Uploaded Documents
              </CardTitle>
              <CardDescription className="mt-1.5">
                View, preview, and manage your uploaded files
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FileList />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Sliding Upload Panel - from right to left */}
      <>
        {/* Backdrop */}
        <div
          role="presentation"
          aria-hidden="true"
          className={cn(
            'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300',
            uploadPanelOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
          onClick={() => setUploadPanelOpen(false)}
        />
        {/* Panel */}
        <div
          className={cn(
            'fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white text-black border-l-2 border-zinc-300 shadow-2xl',
            'flex flex-col transition-transform duration-300 ease-out',
            uploadPanelOpen ? 'translate-x-0' : 'translate-x-full'
          )}
          aria-modal="true"
          aria-label="Upload documents"
        >
          <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-black">
              <Upload className="h-5 w-5 text-primary" />
              Upload Documents
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setUploadPanelOpen(false)}
              aria-label="Close upload panel"
              className="text-black hover:text-black hover:bg-zinc-100"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <p className="text-sm text-zinc-600">
              Drag and drop files or click to browse. Supported: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG
            </p>
            <FileDropzone onUploadComplete={refreshDocuments} />
            <FileUploadProgress />
          </div>
        </div>
      </>
    </MainLayout>
  );
};
